/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../platform/Platform';
import { ComponentManagerFn } from '../../../registry/Component.type';
import { ItemType } from '../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../registry/Components';
import { defaultValue } from './AreaToolbar';

const AreaToolbarManager: ComponentManagerFn<'AreaToolbar'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'AreaToolbar'>(platform, 'AreaToolbar'),
    type: 'AreaToolbar',
    name: 'Area Toolbar',
    isChildAllowed: (page, item, childType, { appUid, pageId }) => {
      if (
        !(
          [
            'AreaToolbarEdit',
            'AreaToolbarSave',
            'AreaToolbarUndoRedo',
            'AreaToolbarZoom',
            'IconButton',
            'ToolbarDivider',
          ] as ItemType[]
        ).includes(childType)
      ) {
        return false;
      }

      if (
        (
          [
            'AreaToolbarEdit',
            'AreaToolbarSave',
            'AreaToolbarUndoRedo',
            'AreaToolbarZoom',
          ] as ItemType[]
        ).includes(childType)
      ) {
        const children = platform.getChildrenOfType(
          appUid,
          pageId,
          page,
          item,
          childType,
        );
        return children.length === 0 ? true : 'Already added';
      }
      return true;
    },
    defaultValue,
    props: {},
    category: 'IoT',
    icon: { iconName: 'rectangle-wide' },
    defaultChildren: ['AreaToolbarEdit'],
    canHaveChildren: () => true,
    description: `**Overview**<br/>
The Area Toolbar is a container component designed specifically for the **IoT Image Mapper** interface. It acts as the control panel that holds various tools for interacting with the map, such as drawing new zones, saving changes, zooming, or undoing actions.
Use this component to provide the user interface for editing and navigating your floor plans or image maps.

**1. Structure & Layout**<br/>
The Area Toolbar itself has no visual properties; it is purely a horizontal container that groups button controls together at the top of the map interface.
* **Default Children:** When you add this component, it typically pre-loads with common tools like **AreaToolbarEdit**.
* **Layout:** Buttons are arranged horizontally, typically aligned to the left or right depending on the parent configuration.

**2. Supported Tools (Children)**<br/>
You can customize the toolbar by adding specific child components to enable or disable features for the end-user.
* **AreaToolbarEdit:** Adds the "Draw / Edit" pencil toggle, allowing users to create or modify polygon shapes.
* **AreaToolbarSave:** Adds the "Save" button to commit geometric changes to the backend.
* **AreaToolbarUndoRedo:** Adds "Undo" and "Redo" arrows to revert drawing mistakes during the editing process.
* **AreaToolbarZoom:** Adds "Zoom In (+)" and "Zoom Out (-)" controls for the map canvas.
* **IconButton / ToolbarDivider:** Generic buttons and separators to add custom actions or visual spacing between tool groups.

**3. Constraints**<br/>
* **Singletons:** You can typically add only one of each specialized tool type (e.g., you cannot add two "Zoom" control sets to the same toolbar).
* **Context:** This toolbar only functions when nested within or linked to an **IoT Image Mapper** parent.

**4. Best Use Cases**<br/>
* **Admin Mode:** Creating a comprehensive toolbar with Edit, Save, and Undo controls for facility managers who need to update floor plans or zone definitions.
* **Viewer Mode:** Creating a simplified toolbar with only Zoom controls for general users who only need to navigate and view the map.
* **Asset Tracking:** Adding custom buttons to the toolbar to toggle different layers of data visibility on the map.`,
  };
};

export default AreaToolbarManager;
